/**
 * DisclosureEditionsManager — Admin hub tab for statutory disclosure-form
 * editions (WI Real Estate Condition Report, WI Vacant Land Disclosure Report).
 *
 * Left: each form with its editions (newest first), the edition new reports
 * use, and how many listings need the form. Right: the selected edition's
 * record, its full text read-only (items by section), raw YAML and change
 * history.
 *
 * Admins change STATUS only (retire / re-activate / activate a draft). Every
 * change opens a confirm dialog that previews the effect (which edition new
 * reports will use afterwards, live listings affected), requires a note, and,
 * when the change would leave a form with no active edition while live
 * listings need it, an explicit confirmation. The form text cannot be edited
 * here: wording changes ship as a new edition through a reviewed PR.
 *
 * Contract: src/agents/disclosureEditionsAdminAgent.js.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import { disclosureEditionsAdminAgent } from '../../agents/disclosureEditionsAdminAgent';
import styles from './DisclosureEditionsManager.module.css';

export const READ_ONLY_NOTICE =
  "Form text can't be edited here. A wording change ships as a new edition through a reviewed pull request and its migration. You can only change an edition's status.";
export const COMPLETED_UNAFFECTED_TEXT =
  'Completed reports are unaffected: they always keep the edition they were signed on.';

const ACTION_LABELS = {
  retire: 'Retire',
  reactivate: 'Re-activate',
  activate: 'Activate',
};

const ACTION_TITLES = {
  retire: 'Retire edition',
  reactivate: 'Re-activate edition',
  activate: 'Activate draft edition',
};

const STATUS_LABELS = { active: 'Active', retired: 'Retired', draft: 'Draft' };

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const editionName = (e) => (e ? `${e.edition} (version ${e.version})` : 'none');

/** Admin-facing message for a failed request. */
export function errorMessage(err, fallback) {
  if (err?.status === 403) return 'You need admin access to manage disclosure editions.';
  const detail = err?.detail;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') return detail.message;
  return err?.message || fallback;
}

function StatusPill({ status }) {
  return (
    <span className={`${styles.pill} ${styles[`pill_${status}`] || ''}`} data-testid="edition-status">
      {STATUS_LABELS[status] || status}
    </span>
  );
}

/** The confirm dialog: effect preview, required note, no-active confirmation. */
function ChangeDialog({ target, onClose, onDone }) {
  const uid = useId();
  const noteRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState(null);
  const [note, setNote] = useState('');
  const [confirmNoActive, setConfirmNoActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [problems, setProblems] = useState([]);

  const { edition, action } = target;

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      setPreview(await disclosureEditionsAdminAgent.preview(edition.id, action.to_status));
    } catch (err) {
      setPreviewError(errorMessage(err, 'The effect of this change could not be loaded.'));
    } finally {
      setPreviewLoading(false);
    }
  }, [edition.id, action.to_status]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const blocked = [...(preview?.blocked_reasons || []), ...problems];
  const needsConfirm = Boolean(preview?.requires_confirmation);
  const noteMissing = !note.trim();
  const canSubmit =
    !saving && !previewLoading && !previewError && blocked.length === 0 && !noteMissing &&
    (!needsConfirm || confirmNoActive);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await disclosureEditionsAdminAgent.changeStatus(edition.id, {
        toStatus: action.to_status,
        note,
        confirmNoActive,
        expectedStatus: edition.status,
      });
      onDone(result);
    } catch (err) {
      const code = err?.detail?.code;
      if (code === 'no_active_edition') {
        // Live listings appeared since the preview: refresh it to show the confirm.
        await loadPreview();
      }
      if (code === 'activation_blocked' && Array.isArray(err.detail.problems)) {
        setProblems(err.detail.problems);
      }
      setSaveError(errorMessage(err, 'The change could not be saved. Nothing was changed.'));
    } finally {
      setSaving(false);
    }
  };

  const after = preview?.current_after;

  return (
    <Modal
      open
      onClose={saving ? () => {} : onClose}
      title={`${ACTION_TITLES[action.action] || 'Change edition'}: ${edition.edition}`}
      size="md"
      initialFocusRef={noteRef}
      dismissOnOverlayClick={false}
    >
      <form onSubmit={submit} data-testid="edition-change-dialog">
        <p className={styles.confirmText}>
          {edition.title || `${edition.jurisdiction} ${edition.form.toUpperCase()}`}, edition{' '}
          <strong>{edition.edition}</strong> (version {edition.version}): {STATUS_LABELS[edition.status]} →{' '}
          <strong>{STATUS_LABELS[action.to_status]}</strong>
        </p>

        <section className={styles.effect} aria-labelledby={`${uid}-effect`} aria-busy={previewLoading}>
          <h4 id={`${uid}-effect`} className={styles.effectTitle}>What this changes</h4>
          {previewLoading && <p className={styles.muted} role="status">Checking the effect…</p>}
          {previewError && (
            <div className={styles.errorBox} role="alert">
              {previewError}{' '}
              <button type="button" className={styles.linkBtn} onClick={loadPreview}>Retry</button>
            </div>
          )}
          {preview && !previewLoading && (
            <dl className={styles.effectList} data-testid="edition-effect">
              <dt>New reports use now</dt>
              <dd>{editionName(preview.current_before)}</dd>
              <dt>New reports will use</dt>
              <dd data-testid="edition-effect-after">
                {after ? (
                  <strong>{editionName(after)}</strong>
                ) : (
                  <strong className={styles.dangerText}>
                    No edition. Sellers can&apos;t start or sign this report until one is active.
                  </strong>
                )}
                {preview.changes_current === false && after ? ' (unchanged)' : ''}
              </dd>
              <dt>Live listings without a completed report</dt>
              <dd data-testid="edition-effect-blocked-listings">
                <strong>{preview.live_listings_without_report ?? 0}</strong>
                {preview.leaves_no_active_edition && preview.live_listings_without_report > 0
                  ? " — these sellers can't start or sign the report while no edition is active"
                  : ''}
              </dd>
              <dt>All listings using this form</dt>
              <dd data-testid="edition-effect-all-listings">
                {preview.live_listings} live (
                {preview.live_listings - (preview.live_listings_without_report ?? 0)} already have a
                completed report), {preview.draft_listings} draft
              </dd>
            </dl>
          )}
          {preview?.after_issuable_problem && (
            <p className={styles.warning} data-testid="edition-effect-issuable">
              Production won&apos;t issue {after?.edition}: {preview.after_issuable_problem}
            </p>
          )}
          <p className={styles.unaffected}>{COMPLETED_UNAFFECTED_TEXT}</p>
        </section>

        {blocked.length > 0 && (
          <div className={styles.errorBox} role="alert" data-testid="edition-blocked">
            <strong>This edition can&apos;t be activated:</strong>
            <ul className={styles.problemList}>
              {blocked.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </div>
        )}

        {needsConfirm && (
          <label className={styles.confirmCheck} data-testid="edition-confirm-no-active">
            <input
              type="checkbox"
              checked={confirmNoActive}
              onChange={(e) => setConfirmNoActive(e.target.checked)}
            />
            <span>
              I understand this leaves the form with no active edition while{' '}
              <strong>
                {preview.live_listings_without_report} live listing
                {preview.live_listings_without_report === 1 ? '' : 's'}
              </strong>{' '}
              still need{preview.live_listings_without_report === 1 ? 's' : ''} a completed report on it.
            </span>
          </label>
        )}

        <label className={styles.label} htmlFor={`${uid}-note`}>Why are you making this change? (required)</label>
        <textarea
          id={`${uid}-note`}
          ref={noteRef}
          className={styles.textarea}
          rows={3}
          maxLength={1000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          aria-required="true"
          aria-describedby={`${uid}-note-hint`}
          data-testid="edition-change-note"
        />
        <p className={styles.hint} id={`${uid}-note-hint`}>
          Kept in the change history with your name and the time.
        </p>

        {saveError && (
          <p className={styles.errorBox} role="alert" data-testid="edition-change-error">{saveError}</p>
        )}

        <div className={styles.confirmActions}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="submit"
            className={`${styles.primaryBtn} ${action.to_status === 'retired' ? styles.dangerBtn : ''}`}
            disabled={!canSubmit}
            data-testid="edition-change-submit"
          >
            {saving ? 'Saving…' : ACTION_LABELS[action.action] || 'Confirm'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditionText({ detail }) {
  if (detail.parse_error) {
    return (
      <p className={styles.errorBox} role="alert" data-testid="edition-parse-error">
        This edition can&apos;t be read: {detail.parse_error}
      </p>
    );
  }
  return (
    <div className={styles.formText} data-testid="edition-form-text">
      {(detail.sections || []).map((section) => (
        <section key={section.code} className={styles.formSection} aria-labelledby={`sec-${section.code}`}>
          <h4 id={`sec-${section.code}`} className={styles.formSectionHeading}>{section.heading}</h4>
          <ol className={styles.itemList}>
            {section.items.map((item) => (
              <li key={item.id} className={styles.item} data-testid={`edition-item-${item.id}`}>
                {item.heading && <p className={styles.itemHeading}>{item.heading}</p>}
                <div className={styles.itemRow}>
                  <span className={styles.itemLabel}>{item.label}</span>
                  <p className={styles.itemText}>{item.text}</p>
                </div>
                <p className={styles.itemMeta}>
                  {item.id} · {item.tag}
                  {item.answerable ? ` · answer: ${item.response_domain}` : ''}
                  {item.effective_from ? ` · in force from ${item.effective_from}` : ''}
                  {item.effective_to ? ` to ${item.effective_to}` : ''}
                  {item.tag === 'statutory' && !item.text_locked ? ' · not locked' : ''}
                </p>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

function EditionDetail({ editionId, reloadKey, onAction }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await disclosureEditionsAdminAgent.get(editionId));
    } catch (err) {
      setDetail(null);
      setError(errorMessage(err, 'The edition could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [editionId]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  if (loading && !detail) {
    return <p className={styles.muted} role="status" data-testid="edition-detail-loading">Loading edition…</p>;
  }
  if (error) {
    return (
      <div className={styles.errorBox} role="alert" data-testid="edition-detail-error">
        {error}{' '}
        <button type="button" className={styles.linkBtn} onClick={load}>Retry</button>
      </div>
    );
  }
  if (!detail) return null;

  const history = detail.history || [];

  return (
    <article className={styles.detail} data-testid="edition-detail" aria-busy={loading}>
      <header className={styles.detailHeader}>
        <div>
          <p className={styles.eyebrow}>{detail.title || `${detail.jurisdiction} ${detail.form.toUpperCase()}`}</p>
          <h3 className={styles.detailTitle}>
            Edition {detail.edition}
            <span className={styles.detailVersion}>version {detail.version}</span>
          </h3>
        </div>
        <div className={styles.detailBadges}>
          <StatusPill status={detail.status} />
          {detail.is_current && <span className={styles.currentTag}>Used for new reports</span>}
        </div>
      </header>

      <dl className={styles.facts}>
        <div><dt>Statutory reference</dt><dd>{detail.statutory_ref || '—'}</dd></div>
        <div>
          <dt>Source</dt>
          <dd>
            {detail.source_url ? (
              <a href={detail.source_url} target="_blank" rel="noopener noreferrer" className={styles.link}>
                {detail.source_url}
              </a>
            ) : '—'}
          </dd>
        </div>
        <div>
          <dt>Content SHA-256</dt>
          <dd><code title={detail.content_sha256} className={styles.code}>{detail.content_sha256_short}</code></dd>
        </div>
        <div><dt>Text transcribed</dt><dd>{detail.text_transcribed ? 'Yes, verbatim' : 'No'}</dd></div>
        <div><dt>Published</dt><dd>{formatDateTime(detail.created_at)}</dd></div>
        <div><dt>Last updated</dt><dd>{formatDateTime(detail.updated_at)}</dd></div>
        <div>
          <dt>Reports on this edition</dt>
          <dd data-testid="edition-report-counts">
            {detail.reports.draft} draft, {detail.reports.completed} completed
            {detail.reports.voided ? `, ${detail.reports.voided} voided` : ''}
          </dd>
        </div>
      </dl>

      {detail.issuable_problem && !detail.parse_error && (
        <p className={styles.warning} data-testid="edition-issuable-problem">
          Production won&apos;t issue this edition: {detail.issuable_problem}
        </p>
      )}

      <div className={styles.actions}>
        {(detail.actions || []).map((action) => (
          <div key={action.action} className={styles.actionGroup}>
            <button
              type="button"
              className={`${styles.secondaryBtn} ${action.to_status === 'retired' ? styles.dangerOutline : ''}`}
              onClick={() => onAction(detail, action)}
              disabled={action.blocked_reasons.length > 0}
              aria-describedby={action.blocked_reasons.length ? `blocked-${detail.id}` : undefined}
              data-testid={`edition-action-${action.action}`}
            >
              {ACTION_LABELS[action.action] || action.action}
            </button>
            {action.blocked_reasons.length > 0 && (
              <ul id={`blocked-${detail.id}`} className={styles.blockedList} data-testid="edition-action-blocked">
                {action.blocked_reasons.map((r) => <li key={r}>{r}</li>)}
              </ul>
            )}
          </div>
        ))}
        {(detail.actions || []).length === 0 && (
          <p className={styles.muted}>No status change is available for this edition.</p>
        )}
      </div>

      <h4 className={styles.subheading}>
        Form text <span className={styles.muted}>(read-only, {detail.item_count} items)</span>
      </h4>
      <EditionText detail={detail} />

      {detail.source_yaml && (
        <details className={styles.details}>
          <summary>Raw YAML</summary>
          <pre className={styles.yaml} data-testid="edition-yaml">{detail.source_yaml}</pre>
        </details>
      )}

      <details className={styles.details} open={history.length > 0} data-testid="edition-history">
        <summary>Change history ({history.length})</summary>
        {history.length === 0 ? (
          <p className={styles.muted}>No admin changes yet. It was published by a migration.</p>
        ) : (
          <ol className={styles.historyList}>
            {history.map((h) => (
              <li key={h.id} className={styles.historyItem}>
                <span className={styles.historyWhen}>{formatDateTime(h.changed_at)}</span>
                <span className={styles.historyWho}>{h.changed_by_name || h.changed_by_email || 'Unknown'}</span>
                <span className={styles.historyChange}>
                  {STATUS_LABELS[h.old_status] || h.old_status} → {STATUS_LABELS[h.new_status] || h.new_status}
                </span>
                <span className={styles.historyNote}>
                  {h.note}
                  <span className={styles.muted}>
                    {' '}New reports: {h.current_edition_before || 'none'} → {h.current_edition_after || 'none'}.
                    {h.confirmed_no_active
                      ? ` Confirmed with ${h.live_listings_affected} live listings still needing a report.`
                      : ''}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </details>
    </article>
  );
}

export default function DisclosureEditionsManager() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [target, setTarget] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [announcement, setAnnouncement] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await disclosureEditionsAdminAgent.list();
      if (!result || !Array.isArray(result.forms)) throw new Error('The editions could not be loaded.');
      setData(result);
      setSelectedId((prev) => {
        const all = result.forms.flatMap((f) => f.editions);
        if (prev && all.some((e) => e.id === prev)) return prev;
        const current = result.forms.find((f) => f.current_edition)?.current_edition;
        return current?.id ?? all[0]?.id ?? null;
      });
    } catch (err) {
      setError(errorMessage(err, 'The editions could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDone = (result) => {
    const change = result?.change;
    setTarget(null);
    setAnnouncement(
      change
        ? `Edition ${change.edition} is now ${STATUS_LABELS[change.new_status] || change.new_status}. New reports use ${change.current_edition_after || 'no edition'}.`
        : 'Saved.',
    );
    setReloadKey((k) => k + 1);
    load();
  };

  const forms = data?.forms || [];

  return (
    <div className={styles.manager} data-testid="disclosure-editions-manager">
      <div className={styles.visuallyHidden} aria-live="polite" aria-atomic="true" data-testid="editions-announcer">
        {announcement}
      </div>

      <div className={styles.toolbar}>
        <h2 className={styles.heading}>Disclosure editions</h2>
        <button type="button" className={styles.secondaryBtn} onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      <p className={styles.notice} data-testid="editions-read-only-notice">{READ_ONLY_NOTICE}</p>

      {announcement && (
        <p className={styles.success} role="status" data-testid="editions-success">{announcement}</p>
      )}

      {loading && !data && <p className={styles.muted} role="status" data-testid="editions-loading">Loading editions…</p>}
      {error && (
        <div className={styles.errorBox} role="alert" data-testid="editions-error">
          {error}{' '}
          <button type="button" className={styles.linkBtn} onClick={load}>Retry</button>
        </div>
      )}

      {data && (
        <div className={styles.layout}>
          <nav className={styles.forms} aria-label="Disclosure forms and editions">
            {forms.length === 0 && <p className={styles.muted}>No disclosure forms are registered.</p>}
            {forms.map((f) => (
              <section
                key={`${f.jurisdiction}-${f.form}`}
                className={styles.formCard}
                data-testid={`editions-form-${f.jurisdiction}-${f.form}`}
                aria-labelledby={`form-${f.jurisdiction}-${f.form}`}
              >
                <h3 id={`form-${f.jurisdiction}-${f.form}`} className={styles.formTitle}>{f.title}</h3>
                <p className={styles.formMeta} data-testid="editions-form-listings">
                  {f.statutory_ref || `${f.jurisdiction} ${f.form.toUpperCase()}`}
                  {' · '}
                  {f.listings.live} live ({f.listings.live_without_report ?? 0} without a completed
                  report), {f.listings.draft} draft
                </p>
                <p className={styles.formCurrent} data-testid="editions-form-current">
                  New reports use:{' '}
                  {f.current_edition ? (
                    <strong>{editionName(f.current_edition)}</strong>
                  ) : f.published ? (
                    <strong className={styles.dangerText}>no active edition</strong>
                  ) : (
                    <span>
                      not published yet; the app reads the reviewed file
                      {f.file_fallback?.edition ? ` (${f.file_fallback.edition})` : ''}
                    </span>
                  )}
                </p>
                <ul className={styles.editionList}>
                  {f.editions.map((e) => (
                    <li key={e.id}>
                      <button
                        type="button"
                        className={`${styles.editionItem} ${e.id === selectedId ? styles.editionItemActive : ''} ${styles[`edge_${e.status}`] || ''}`}
                        onClick={() => setSelectedId(e.id)}
                        aria-current={e.id === selectedId ? 'true' : undefined}
                        data-testid={`edition-item-button-${e.id}`}
                      >
                        <span className={styles.editionTop}>
                          <span className={styles.editionName}>
                            {e.edition} <span className={styles.muted}>v{e.version}</span>
                          </span>
                          <StatusPill status={e.status} />
                        </span>
                        <span className={styles.editionMeta}>
                          {e.is_current ? 'Used for new reports · ' : ''}
                          {e.reports.draft} draft, {e.reports.completed} completed
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </nav>

          <section className={styles.detailPane} aria-label="Selected edition">
            {selectedId ? (
              <EditionDetail
                editionId={selectedId}
                reloadKey={reloadKey}
                onAction={(edition, action) => setTarget({ edition, action })}
              />
            ) : (
              <p className={styles.muted}>Select an edition to see its text and history.</p>
            )}
          </section>
        </div>
      )}

      {target && <ChangeDialog target={target} onClose={() => setTarget(null)} onDone={handleDone} />}
    </div>
  );
}
